import React, { useState, useEffect, useCallback } from 'react';

interface YouTubeVideo {
  /** YouTube video URL or embed URL */
  url: string;
  /** Video title displayed as caption */
  title: string;
  /** Optional description */
  description?: string;
  /** Enable autoplay when video is active (default: true) */
  autoplay?: boolean;
  /** Enable muted autoplay (required by most browsers for autoplay) (default: true) */
  muted?: boolean;
  /** Enable loop playback (default: false) */
  loop?: boolean;
  /** Start time in seconds */
  startTime?: number;
  /** End time in seconds */
  endTime?: number;
  /** Thumbnail URL override (if not provided, extracted from YouTube) */
  thumbnailUrl?: string;
}

interface YouTubeVideoCarouselProps {
  /** Array of YouTube video configurations */
  videos?: any[];
  /** Auto-play interval in milliseconds (default: 10000) */
  autoPlayInterval?: number;
  /** Section title */
  title?: string;
}

/**
 * Converts a standard YouTube URL to an embed URL with optional autoplay config
 */
const getEmbedUrl = (video: any, autoplay: boolean = true): string => {
  let videoId = '';
  let url = video.url || '';

  // Handle already embed URLs
  if (url.includes('youtube.com/embed/')) {
    const embedPath = url.split('youtube.com/embed/')[1];
    videoId = embedPath.split('?')[0].split('/')[0];
  }
  // Handle standard YouTube URLs
  else if (url.includes('youtube.com/watch')) {
    const urlObj = new URL(url);
    videoId = urlObj.searchParams.get('v') || '';
  }
  // Handle youtu.be short URLs
  else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0]?.split('/')[0] || '';
  }
  // Handle bare video IDs
  else if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) {
    videoId = url.trim();
  }

  if (!videoId) {
    console.warn('Invalid YouTube URL:', url);
    return '';
  }

  const params = new URLSearchParams();
  if (autoplay) params.set('autoplay', '1');
  // Browser autoplay policy REQUIRES muted video for autoplay to work on HTTPS.
  // Without mute=1, Chrome/Firefox/Safari will block autoplay entirely on production.
  // The 'muted' prop controls initial sound state, but mute=1 is mandatory for autoplay.
  if (autoplay) params.set('mute', '1');
  if (video.loop) params.set('loop', '1');
  if (video.startTime) params.set('start', String(video.startTime));
  if (video.endTime) params.set('end', String(video.endTime));
  params.set('rel', '0');
  params.set('modestbranding', '1');

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

const YouTubeVideoCarousel: React.FC<YouTubeVideoCarouselProps> = ({
  videos = [],
  autoPlayInterval = 10000,
  title = 'Featured Videos',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    if (videos.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % videos.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [videos.length]);

  const prevSlide = useCallback(() => {
    if (videos.length <= 1 || isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [videos.length, isTransitioning]);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  // Auto-advance
  useEffect(() => {
    if (videos.length <= 1) return;
    const interval = setInterval(nextSlide, autoPlayInterval);
    return () => clearInterval(interval);
  }, [videos.length, nextSlide, autoPlayInterval]);

  if (!videos || videos.length === 0) {
    return null;
  }

  const currentVideo = videos[currentIndex];

  // Inline styles matching EventCarousel size
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: '#000',
    boxShadow: '0 8px 24px rgb(14 165 233 / 0.15)',
  };

  const iframeContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  };

  const captionStyle: React.CSSProperties = {
    marginTop: '8px',
    padding: 'clamp(10px, 3.5vw, 20px) clamp(14px, 5vw, 30px)',
    background: 'rgba(255, 255, 255, 0.85)',
    borderRadius: '12px',
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 'clamp(1rem, 5.5vw, 1.3rem)',
    lineHeight: 1.15,
    fontWeight: 800,
    color: '#0369a1',
  };

  const descStyle: React.CSSProperties = {
    margin: '4px 0 0 0',
    fontSize: 'clamp(0.8rem, 3.6vw, 0.95rem)',
    lineHeight: 1.25,
    color: '#475569',
  };

  const arrowBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255, 255, 255, 0.85)',
    border: 'none',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    cursor: 'pointer',
    zIndex: 10,
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    display: videos.length <= 1 ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <>
      <style>{`
        .yt-video-carousel-wrapper {
          padding: 0;
          background: transparent;
          border-radius: 18px;
        }
        .yt-video-carousel-wrapper h3 {
          color: white;
          margin-top: 0;
          margin-bottom: 16px;
          font-weight: 800;
          font-size: 1.5rem;
        }
        .yt-video-carousel-wrapper .yt-arrow:hover:not(:disabled) {
          background: white;
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
        }
        .yt-video-carousel-wrapper .yt-arrow:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: translateY(-50%) scale(0.9);
        }
        .yt-video-carousel-wrapper .yt-indicators {
          position: absolute;
          bottom: 12px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 5;
        }
        .yt-video-carousel-wrapper .yt-indicator-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }
        .yt-video-carousel-wrapper .yt-indicator-dot:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: scale(1.2);
        }
        .yt-video-carousel-wrapper .yt-indicator-dot.active {
          background: #0f766e;
          box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.3);
          transform: scale(1.3);
        }
        .yt-video-carousel-wrapper .yt-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          transition: opacity 0.7s ease;
          pointer-events: none;
        }
        .yt-video-carousel-wrapper .yt-slide.active {
          opacity: 1;
          position: relative;
          pointer-events: auto;
        }
        @media (max-width: 768px) {
          .yt-video-carousel-wrapper h3 {
            font-size: 1.35rem;
            margin-bottom: 12px;
          }
          .yt-video-carousel-wrapper .yt-arrow {
            width: 36px !important;
            height: 36px !important;
            font-size: 22px !important;
          }
          .yt-video-carousel-wrapper .yt-indicators {
            gap: 8px;
          }
          .yt-video-carousel-wrapper .yt-indicator-dot {
            width: 10px;
            height: 10px;
          }
        }

        @media (max-width: 480px) {
          .yt-video-carousel-wrapper h3 {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <div className="yt-video-carousel-wrapper" aria-label={title}>
        <h3>{title}</h3>
        <div style={containerStyle} role="region" aria-roledescription="carousel" aria-label="YouTube video carousel">
          {/* Navigation Arrows */}
          {videos.length > 1 && (
            <button
              className="yt-arrow"
              style={{ ...arrowBaseStyle, left: '16px' }}
              onClick={prevSlide}
              aria-label="Previous video"
              disabled={isTransitioning}
            >
              ‹
            </button>
          )}
          {videos.length > 1 && (
            <button
              className="yt-arrow"
              style={{ ...arrowBaseStyle, right: '16px' }}
              onClick={nextSlide}
              aria-label="Next video"
              disabled={isTransitioning}
            >
              ›
            </button>
          )}

          {/* Video Slides */}
          {videos.map((video: any, index: number) => {
            const embedUrl = getEmbedUrl(video, index === currentIndex && (video.autoplay !== false));
            return (
              <div
                key={index}
                className={`yt-slide ${index === currentIndex ? 'active' : ''}`}
                role="group"
                aria-roledescription="slide"
                aria-label={`Video ${index + 1} of ${videos.length}: ${video.title || 'Untitled'}`}
              >
                {embedUrl ? (
                  <div style={iframeContainerStyle}>
                    <iframe
                      width="100%"
                      height="100%"
                      src={embedUrl}
                      title={video.title || `Video ${index + 1}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      style={{ border: 'none' }}
                    />
                  </div>
                ) : (
                  <div style={{
                    ...iframeContainerStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#1a1a2e',
                    color: 'white',
                    fontSize: '1.1rem',
                  }}>
                    Video unavailable
                  </div>
                )}

              </div>
            );
          })}

          {/* Indicator Dots */}
          {videos.length > 1 && (
            <div className="yt-indicators" role="tablist" aria-label="Video indicators">
              {videos.map((_: any, index: number) => (
                <button
                  key={index}
                  className={`yt-indicator-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to video ${index + 1}`}
                  aria-selected={index === currentIndex ? 'true' : 'false'}
                  role="tab"
                />
              ))}
            </div>
          )}
        </div>
        {(currentVideo.title || currentVideo.description) && (
          <div style={captionStyle}>
            {currentVideo.title && <div style={titleStyle}>{currentVideo.title}</div>}
            {currentVideo.description && <div style={descStyle}>{currentVideo.description}</div>}
          </div>
        )}
      </div>
    </>
  );
};

export default YouTubeVideoCarousel;
